import { useState, useEffect } from 'react';
import { 
  Server, 
  Play, 
  Square, 
  Trash2, 
  Plus, 
  Cpu, 
  HardDrive, 
  MemoryStick,
  Globe,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { devContainerAPI } from '@/lib/dev-containers/api';
import { useAuth } from '@/contexts/AuthContext';

interface Workspace {
  id: string;
  name: string;
  status: 'provisioning' | 'starting' | 'running' | 'stopping' | 'stopped' | 'terminated' | 'error';
  specs: {
    ocpu: number;
    memory: number;
    storage: number;
  };
  subdomain: string;
  projects: any[];
  lastAccessedAt: Date;
  createdAt: Date;
  errorMessage?: string;
  ociResources?: {
    privateIp?: string;
    publicIp?: string;
  };
}

export default function DevContainersFull() {
  const { user } = useAuth();
  const [devContainers, setDevContainers] = useState<Workspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);
  const [actionLoading, setActionLoading] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState({
    name: '',
    ocpu: 2,
    memory: 4,
    storage: 100,
  });

  useEffect(() => {
    loadDevContainers();
  }, []);

  const loadDevContainers = async () => {
    try {
      setLoading(true);
      const data = await devContainerAPI.getUserDevContainers();
      setDevContainers(data);
    } catch (error) {
      console.error('Failed to load devContainers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkspace = async () => {
    if (!formData.name.trim()) return;

    setActionLoading({ creating: 'true' });
    try {
      const workspace = await devContainerAPI.createDevContainer({
        name: formData.name,
        ocpu: formData.ocpu,
        memory: formData.memory,
        storage: formData.storage,
      });
      
      setDevContainers([workspace, ...devContainers]);
      setShowCreateModal(false);
      setFormData({ name: '', ocpu: 2, memory: 4, storage: 32 });
    } catch (error) {
      console.error('Failed to create workspace:', error);
    } finally {
      setActionLoading({});
    }
  };

  const handleStartWorkspace = async (workspace: Workspace) => {
    setActionLoading({ [workspace.id]: 'starting' });
    try {
      const updated = await devContainerAPI.startDevContainer(workspace.id);
      setDevContainers(devContainers.map(w => w.id === updated.id ? updated : w));
    } catch (error) {
      console.error('Failed to start workspace:', error);
    } finally {
      setActionLoading({});
    }
  };

  const handleStopWorkspace = async (workspace: Workspace) => {
    setActionLoading({ [workspace.id]: 'stopping' });
    try {
      const updated = await devContainerAPI.stopDevContainer(workspace.id);
      setDevContainers(devContainers.map(w => w.id === updated.id ? updated : w));
    } catch (error) {
      console.error('Failed to stop workspace:', error);
    } finally {
      setActionLoading({});
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!selectedWorkspace) return;

    setActionLoading({ [selectedWorkspace.id]: 'deleting' });
    try {
      await devContainerAPI.deleteDevContainer(selectedWorkspace.id);
      setDevContainers(devContainers.filter(w => w.id !== selectedWorkspace.id));
      setShowDeleteModal(false);
      setSelectedWorkspace(null);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
    } finally {
      setActionLoading({});
    }
  };

  const getStatusBadge = (status: Workspace['status']) => {
    const statusConfig = {
      provisioning: { color: 'bg-yellow-100 text-yellow-800', label: 'Provisioning' },
      starting: { color: 'bg-blue-100 text-blue-800', label: 'Starting' },
      running: { color: 'bg-green-100 text-green-800', label: 'Running' },
      stopping: { color: 'bg-orange-100 text-orange-800', label: 'Stopping' },
      stopped: { color: 'bg-gray-100 text-gray-800', label: 'Stopped' },
      terminated: { color: 'bg-red-100 text-red-800', label: 'Terminated' },
      error: { color: 'bg-red-100 text-red-800', label: 'Error' },
    };

    const config = statusConfig[status];
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const then = new Date(date);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  return (
    <div className="min-h-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 lg:py-16">
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">Container Workspaces</h1>
              <p className="text-muted-foreground text-base sm:text-lg">
                Manage your development containers with VS Code environments
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              disabled={!!actionLoading.creating}
              className="sm:mt-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Workspace
            </Button>
          </div>
        </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : devContainers.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Server className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-semibold mb-2">No Dev Containers Yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first container workspace to get started with Power Mode development
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Workspace
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {devContainers.map(workspace => (
            <Card key={workspace.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Server className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <CardTitle className="text-xl mb-1">
                        {workspace.name}
                        {workspace.name.endsWith(' Workspace') ? '' : ' Workspace'}
                      </CardTitle>
                      <CardDescription>
                        {workspace.subdomain}.dev-sandbox2.optidev.com
                      </CardDescription>
                      {workspace.projects?.length > 0 && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {workspace.projects?.length || 0} project{(workspace.projects?.length || 0) !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(workspace.status)}
                    {actionLoading[workspace.id] && (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {workspace.errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex gap-2">
                      <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {workspace.errorMessage}
                      </p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{workspace.specs.ocpu} OCPU</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MemoryStick className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{workspace.specs.memory} GB RAM</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{workspace.specs.storage} GB Storage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">
                      {workspace.ociResources?.privateIp || 'No IP assigned'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Last accessed {getRelativeTime(workspace.lastAccessedAt)}
                    </span>
                    <span>
                      {workspace.projects?.length || 0} project{(workspace.projects?.length || 0) !== 1 && 's'}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    {workspace.status === 'stopped' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStartWorkspace(workspace)}
                        disabled={!!actionLoading[workspace.id]}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Start
                      </Button>
                    ) : workspace.status === 'running' ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`https://${workspace.subdomain}.dev-sandbox2.optidev.com`, '_blank')}
                        >
                          Open VS Code
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStopWorkspace(workspace)}
                          disabled={!!actionLoading[workspace.id]}
                        >
                          <Square className="w-4 h-4 mr-1" />
                          Stop
                        </Button>
                      </>
                    ) : null}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedWorkspace(workspace);
                        setShowDeleteModal(true);
                      }}
                      disabled={!!actionLoading[workspace.id]}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Workspace Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Workspace"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My Development Environment"
              className="mt-1"
              autoFocus
            />
          </div>

          <div>
            <Label>Resources</Label>
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="p-3 border rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Cpu className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">CPU</span>
                </div>
                <select
                  value={formData.ocpu}
                  onChange={(e) => setFormData({ ...formData, ocpu: Number(e.target.value) })}
                  className="w-full text-sm bg-transparent border-0 focus:ring-0"
                >
                  <option value={1}>1 OCPU</option>
                  <option value={2}>2 OCPU</option>
                  <option value={4}>4 OCPU</option>
                  <option value={8}>8 OCPU</option>
                </select>
              </div>

              <div className="p-3 border rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <MemoryStick className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">RAM</span>
                </div>
                <select
                  value={formData.memory}
                  onChange={(e) => setFormData({ ...formData, memory: Number(e.target.value) })}
                  className="w-full text-sm bg-transparent border-0 focus:ring-0"
                >
                  <option value={2}>2 GB</option>
                  <option value={4}>4 GB</option>
                  <option value={8}>8 GB</option>
                  <option value={16}>16 GB</option>
                </select>
              </div>

              <div className="p-3 border rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <HardDrive className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Storage</span>
                </div>
                <select
                  value={formData.storage}
                  onChange={(e) => setFormData({ ...formData, storage: Number(e.target.value) })}
                  className="w-full text-sm bg-transparent border-0 focus:ring-0"
                >
                  <option value={50}>50 GB</option>
                  <option value={100}>100 GB</option>
                  <option value={200}>200 GB</option>
                  <option value={500}>500 GB</option>
                </select>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              The workspace will be provisioned in US-CHICAGO-1 region. Provisioning may take 2-3 minutes.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateModal(false)}
              className="flex-1"
              disabled={!!actionLoading.creating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateWorkspace}
              disabled={!formData.name.trim() || !!actionLoading.creating}
              className="flex-1"
            >
              {actionLoading.creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Workspace'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => !actionLoading[selectedWorkspace?.id || ''] && setShowDeleteModal(false)}
        title="Delete Workspace"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <strong>{selectedWorkspace?.name}</strong>?
            This will terminate the container and delete all associated resources.
          </p>
          
          {selectedWorkspace?.projects && selectedWorkspace.projects.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                This workspace has {selectedWorkspace.projects.length} project(s) associated with it.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={!!actionLoading[selectedWorkspace?.id || '']}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteWorkspace}
              disabled={!!actionLoading[selectedWorkspace?.id || '']}
              className="flex-1"
            >
              {actionLoading[selectedWorkspace?.id || ''] ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Workspace'
              )}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
}